import cn from 'classnames';
import getConfig from 'next/config';
import type { FC, ReactNode } from 'react';
import React from 'react';

import { getAntifraudSdkScript } from '@sravni/antifraud-sdk';
import { CookiesPolicyAgreementCard } from '@sravni/react-cookies-policy';

import { MobileAppBanner } from '@src/components/MobileAppBanner';
import { NoScript } from '@src/components/NoScript';
import { PERSISTENT_OFFER_KEY } from '@src/helpers/persistentOffers';
import { useHasUtmLabel } from '@src/reducers/route';
import { TopBanner } from 'entities/Banner';
import type { SeoSchemaConfig } from 'entities/Seo/types';
import { CompanyName } from 'shared/config/constants';
import { useScriptLoader } from 'shared/hooks/useScriptLoader';
import { Footer } from 'widgets/Footer';
import { GlobalDialog } from 'widgets/GlobalDialog/ui/GlobalDialog';
import { Header } from 'widgets/Header';
import { SeoHead } from 'widgets/SeoHead';

import { useInitSentryAndMindbox, useSetHistory } from '../lib';
import { useResetSessionCache } from '../lib/useResetSessionCache';

import { CustomCommonHead } from './CustomCommonHead/CustomCommonHead';
import styles from './Layout.module.scss';

interface Props {
  mode?: 'landing' | 'default';
  footer?: ReactNode;
  header?: ReactNode;

  showMobileAppBanner?: boolean;
  isWhiteLabel?: boolean;

  containerClassName?: string;
  contentClassName?: string;
  seoJsonSchemaConfig?: SeoSchemaConfig;
  cookiePolicyDisabled?: boolean;
  /**
   * default cookie company Sravni
   */
  cookieCompany?: CompanyName;
}

export const Layout: FC<Props> = ({
  children,
  mode = 'default',
  footer = <Footer mode={mode} />,
  header = <Header mode={mode} />,
  showMobileAppBanner = true,
  isWhiteLabel = false,
  containerClassName,
  contentClassName,
  seoJsonSchemaConfig,
  cookiePolicyDisabled,
  cookieCompany = CompanyName.Sravni,
}) => {
  const isContextPage = useHasUtmLabel();

  // todo(MFO-1275): Распределить хуки по фичам
  useInitSentryAndMindbox();
  useSetHistory();

  useResetSessionCache(PERSISTENT_OFFER_KEY);

  const _showMobileAppBanner = !isContextPage && showMobileAppBanner;

  useScriptLoader(getAntifraudSdkScript({ environment: getConfig().publicRuntimeConfig.environment }));

  return (
    <>
      <CustomCommonHead isWhiteLabel={isWhiteLabel} />
      <SeoHead seoJsonSchemaConfig={seoJsonSchemaConfig} />
      <NoScript isWhiteLabel={isWhiteLabel} />

      <div className={containerClassName}>
        <div className={cn(styles.container, contentClassName)}>
          <GlobalDialog />
          <TopBanner />

          {_showMobileAppBanner && <MobileAppBanner />}

          {header}

          {children}
        </div>

        {footer}
        {!cookiePolicyDisabled && <CookiesPolicyAgreementCard companyName={cookieCompany} />}
      </div>
    </>
  );
};
